/**
 * CharacterPortrait Component
 * 
 * Displays a character portrait with WebP/JPEG fallback support and lazy loading.
 * Uses the <picture> element for optimal format selection and provides fallback
 * display when portraits are not available.
 */

import React, { useState } from 'react';
import { portraitService } from '../../services/portraitService';
import type { PortraitVariantSize } from '../../types/portrait';

export interface CharacterPortraitProps {
  /**
   * Portrait ID to display (e.g., 'adventurer_male', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  portraitId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 64x64px (for headers, small displays)
   * - card: 256x256px (for character cards, selection)
   * - large: 512x512px (for detail views, profiles)
   */
  variant?: PortraitVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Data test ID for testing
   */
  'data-testid'?: string;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;

  /**
   * When true, the portrait fills its container (width and height 100%).
   * Use in a fixed-size container (e.g. scene header) so portrait and banner share the same height.
   */
  fillContainer?: boolean;
}

/**
 * Default fallback component when portrait image cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: PortraitVariantSize; alt: string | undefined }> = ({ variant, alt }) => {
  const sizes = {
    thumbnail: 'w-16 h-16',
    card: 'w-64 h-64',
    large: 'w-96 h-96',
  };
  
  const iconSizes = {
    thumbnail: 'text-2xl',
    card: 'text-6xl',
    large: 'text-8xl',
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}
      role="img"
      aria-label={alt || 'Character portrait placeholder'}
    >
      <span className={iconSizes[variant]}>👤</span>
    </div>
  );
};

/**
 * CharacterPortrait component
 */
export const CharacterPortrait: React.FC<CharacterPortraitProps> = ({
  portraitId,
  variant = 'card',
  alt,
  className = '',
  style,
  fallback,
  lazy = true,
  'data-testid': dataTestId,
  fillContainer = false,
}) => {
  // Track which URL failed (not just a boolean)
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Effect to wait for registry to load
  React.useEffect(() => {
    let isMounted = true;
    
    if (!portraitService.isLoaded()) {
      portraitService.loadRegistry().then(() => {
        // Registry loaded successfully
        if (isMounted) {
          // Force a re-render by updating a state
          setIsLoading(false);
        }
      }).catch(() => {
        // Silently handle errors - portraits will fall back to placeholders
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Get portrait URLs from the service
  const urls = portraitService.getPortraitUrls(portraitId, variant);

  // If no URLs, show fallback
  if (!urls) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }
  
  // If THIS specific URL failed, show fallback
  if (failedUrl === urls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }

  // Size classes for the container (when not filling a parent)
  const sizeClasses = {
    thumbnail: 'w-16 h-16',
    card: 'w-64 h-64',
    large: 'w-96 h-96',
  };
  const containerSizeClass = fillContainer ? 'w-full h-full' : sizeClasses[variant];
  const fillStyle = fillContainer ? { width: '100%', height: '100%', minHeight: 0 } : undefined;

  // Handle image load error
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const failedSrc = e.currentTarget.src;
    console.warn(`Failed to load portrait: ${portraitId}`);
    setFailedUrl(failedSrc);
    setIsLoading(false);
  };

  // Handle image load success
  const handleLoad = () => {
    setIsLoading(false);
  };

  // Use URLs as key to force remount when they change
  const imageKey = `${urls.webp}-${urls.jpg}`;

  return (
    <div 
      className={`${containerSizeClass} relative ${className}`}
      data-testid={dataTestId}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...fillStyle,
        ...style
      }}
    >
      {/* Show placeholder while loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 animate-pulse">
          <span className="text-gray-500">Loading...</span>
        </div>
      )}
      
      {/* Picture element with WebP and JPEG sources */}
      {/* Using key prop to force remount when URLs change, clearing error state */}
      <picture key={imageKey} style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* WebP source (modern browsers) */}
        <source
          type="image/webp"
          srcSet={urls.webp}
        />
        
        {/* JPEG fallback (all browsers) */}
        <img
          src={urls.jpg}
          alt={alt || 'Character portrait'}
          loading={lazy ? 'lazy' : 'eager'}
          onError={handleError}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        />
      </picture>
    </div>
  );
};

export default CharacterPortrait;

